import QButton from 'components/QButton';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
/* Standard Imports */
import React from 'react';

/* Material UI */
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

/* Path Relative */
import { fmtAmtWithDecimals } from 'containers/Goals/utils';
import { useStyles } from './styles';

/* Linter Disables */
/* eslint-disable no-restricted-globals */ // this is disabled to use isNaN()

const EditGoalSuggestions = (props) => {
  /* Prop Types */
  EditGoalSuggestions.propTypes = {
    dateInUse: PropTypes.instanceOf(DateTime), // the date that the user has filled out currently
    amount: PropTypes.number, // a $ value representing how much the user must spend to hit goal given selected date
    changeAmount: PropTypes.func, // a function to change the user's monthly contribution
    hasEndDate: PropTypes.bool, // a flag to determine whether user wants to hit goal by spec'd date
    changeDate: PropTypes.func, // a function to change the user's end date
    date: PropTypes.instanceOf(DateTime), // a date representing when user will complete goal given contribution
    hide: PropTypes.bool, // show nothing (height 0)
    invalidContributionAmount: PropTypes.bool,
  };

  /* Component Level Variables */
  const { classes } = useStyles(props);
  const { changeDate, date, hasEndDate, amount, changeAmount, dateInUse, hide, invalidContributionAmount } = props;
  const [wantNewAmount, setWantNewAmount] = React.useState(false);
  const [show, setShow] = React.useState(true);
  const [wantNewDate, setWantNewDate] = React.useState(false);
  const theme = useTheme();
  /* Effects */
  React.useEffect(() => {
    // for animation
    setShow(true);
    // reset checkbox values
    setWantNewAmount(false);
    setWantNewDate(false);
  }, [date, amount]);

  /* Helper Functions */
  // This function will diff the dateInUse and the suggested date to decide if user is 'early'
  const isEarly = () => dateInUse.diff(date, 'months').months >= 0;

  // This function checks if date is 'safe' to display
  const dateValid = (_) => {
    if (!date || invalidContributionAmount) return false;
    const threshold = _ || 50;
    const { years } = date.diff(DateTime.local(), 'years').toObject();
    return DateTime.isDateTime(date) && years < threshold && date.isValid;
  };
  // This function checks if amount is 'safe' to display
  const amountValid = () => amount > 0 && !Number.isNaN(amount) && isFinite(amount);

  /* Partial Render Functions */
  const dateRow = () => {
    const showDate = dateValid();
    return (
      <div className={classes.flexRow} style={{ height: showDate ? 52 : 0, border: !showDate && 'none' }}>
        {showDate && (
          <>
            <Typography style={{ width: '70%' }} variant='bodyMedium'>
              Change date to: {date.toFormat('LLL yyyy')}
            </Typography>
            <QButton
              style={{
                backgroundColor: theme.palette.primary.light,
                borderRadius: 4,
                height: 50,
              }}
              variant='text'
              onClick={() => {
                setWantNewDate(!wantNewDate);
                changeDate(date.toFormat('LLL yyyy'));
                setShow(!show);
              }}
            >
              Change
            </QButton>
          </>
        )}
      </div>
    );
  };

  const amtRow = () => {
    const showAmt = amountValid();
    return (
      <div className={classes.flexRow} style={{ height: showAmt ? 52 : 0, border: !showAmt && 'none' }}>
        {showAmt && (
          <>
            <Typography style={{ width: '70%' }} variant='bodyMedium'>
              Change your monthly contribution to: {fmtAmtWithDecimals(amount)}
            </Typography>
            <QButton
              style={{
                backgroundColor: theme.palette.primary.light,
                borderRadius: 4,
                height: 50,
              }}
              variant='text'
              onClick={() => {
                setWantNewAmount(!wantNewAmount);
                changeAmount(amount);
                setShow(!show);
              }}
            >
              Change
            </QButton>
          </>
        )}
      </div>
    );
  };

  if (!amountValid() && !dateValid()) {
    return null;
  }

  /* Main Render */
  return (
    <div
      className={classes.flexCol}
      style={{
        height:
          (!hide &&
            (hasEndDate
              ? (date && dateInUse ? 45 : 0) +
                (dateValid() ? 52 : 0) +
                (amountValid() ? 52 : 0) +
                (dateValid() && amountValid() ? 16 : 0)
              : 37)) ||
          0,
        padding: hide ? 0 : '8px 16px',
      }}
    >
      {!hide &&
        (hasEndDate ? (
          <>
            {date &&
              dateInUse && ( // null check for date and dateInUse b/c they'll be diff'd
                <Typography className={classes.overlineText} variant='bodyMedium'>
                  {`You'll hit your goal ${isEarly() ? 'early' : 'late'} (click to adjust)`}
                </Typography>
              )}
            {dateRow()}
            <div style={{ height: dateValid() && amountValid() && 16, transition: 'height 0.4s ease' }} />
            {amtRow()}
          </>
        ) : (
          <div className={classes.parentContainer}>
            <Typography variant='bodyMedium'>
              {dateValid(500)
                ? `You will hit your goal by ${date.toFormat('LLLL yyyy')}`
                : "You'll be 💀 before your goal ends"}
            </Typography>
          </div>
        ))}
    </div>
  );
};

export default EditGoalSuggestions;
